import { Box, Typography, useTheme, Button } from "@mui/material";
import { useSelector }   from "react-redux";
import { useState }      from "react";

import AddIcon           from "@mui/icons-material/Add";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaymentsIcon      from "@mui/icons-material/Payments";
import CardGiftcardIcon  from "@mui/icons-material/CardGiftcard";
import RemoveCircleIcon  from "@mui/icons-material/RemoveCircle";

import FixedSizeDialog from "components/fixedSizeDialog";
import RenumerationTypeForm from "../forms/renumerationTypeCreateForm";
import DeductionTypeForm    from "../forms/deductionTypeCreateForm";
import AllowanceTypeForm    from "../forms/allowanceTypeCreateForm";
import { requester }        from "api/pesabackend";
import {
  useRenumerationTypesList,
  useDeductionTypesList,
  useAllowanceTypesList,
} from "api/hooks/humanResource";

/* ── Action button ── */
const ActionBtn = ({ label, icon, onClick, color = "default" }) => {
  const map = {
    default: { color: "#888780", border: "rgba(136,135,128,0.3)", hover: "rgba(136,135,128,0.08)" },
    warning: { color: "#BA7517", border: "rgba(186,117,23,0.3)",  hover: "rgba(186,117,23,0.08)"  },
    danger:  { color: "#e24b4a", border: "rgba(226,75,74,0.3)",   hover: "rgba(226,75,74,0.08)"   },
  };
  const c = map[color] ?? map.default;
  return (
    <Box onClick={onClick} sx={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      px: "8px", py: "4px", borderRadius: "6px",
      border: `1px solid ${c.border}`, cursor: "pointer",
      color: c.color, fontSize: "12px", fontWeight: 500,
      transition: "all 0.15s",
      "&:hover": { backgroundColor: c.hover },
    }}>
      {icon && <Box sx={{ fontSize: "14px", display: "flex" }}>{icon}</Box>}
      {label}
    </Box>
  );
};

/* ── Reusable settings section ── */
const SettingsSection = ({
  icon, title, color = "#1D9E75",
  items, nameKey,
  onAdd, onEdit, onDelete,
  emptyMessage,
  cardBg, border, accent, textPri, textSec, isDark,
}) => (
  <Box sx={{
    backgroundColor: cardBg,
    border: `1px solid ${border}`,
    borderRadius: "12px",
    overflow: "hidden",
  }}>
    {/* Section header */}
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      px: "1.25rem", py: "1rem",
      borderBottom: `1px solid ${border}`,
      backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.04)",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{
          width: "30px", height: "30px", borderRadius: "8px",
          backgroundColor: "rgba(29,158,117,0.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accent, flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{title}</Typography>
          <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>
            {items.length} type{items.length !== 1 ? "s" : ""} configured
          </Typography>
        </Box>
      </Box>

      <Button
        onClick={onAdd}
        startIcon={<AddIcon sx={{ fontSize: "15px" }} />}
        sx={{
          fontSize: "12px", fontWeight: 500,
          px: "0.875rem", py: "0.4rem",
          borderRadius: "8px", textTransform: "none",
          backgroundColor: accent, color: "#fff",
          "&:hover": { backgroundColor: "#0F6E56" },
        }}
      >
        Add {title.replace(" Types", " Type")}
      </Button>
    </Box>

    {/* Items list */}
    <Box>
      {items.length === 0 ? (
        <Box sx={{ px: "1.25rem", py: "1.5rem", textAlign: "center" }}>
          <Typography sx={{ fontSize: "13px", color: textSec }}>{emptyMessage}</Typography>
        </Box>
      ) : (
        items.map((row, index) => (
          <Box
            key={row.id}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: "1.25rem", py: "0.75rem",
              borderBottom: index < items.length - 1 ? `1px solid ${border}` : "none",
              transition: "background 0.1s",
              "&:hover": { backgroundColor: isDark ? "rgba(29,158,117,0.04)" : "rgba(29,158,117,0.03)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box sx={{
                width: "22px", height: "22px", borderRadius: "50%",
                backgroundColor: isDark ? "rgba(29,158,117,0.12)" : "rgba(29,158,117,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <Typography sx={{ fontSize: "10px", fontWeight: 600, color: accent }}>
                  {index + 1}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "13px", color: textPri }}>{row[nameKey]}</Typography>
            </Box>

            <Box sx={{ display: "flex", gap: "6px" }}>
              <ActionBtn
                label="Edit"
                icon={<EditOutlinedIcon fontSize="inherit" />}
                color="warning"
                onClick={() => onEdit(row.id)}
              />
              <ActionBtn
                label="Delete"
                icon={<DeleteOutlineIcon fontSize="inherit" />}
                color="danger"
                onClick={() => onDelete(row.id)}
              />
            </Box>
          </Box>
        ))
      )}
    </Box>
  </Box>
);

/* ── Delete confirmation content ── */
const DeleteContent = ({ onConfirm }) => (
  <Box p="1rem">
    <Typography sx={{ fontSize: "13px", color: "#888780", mb: "1rem" }}>
      This action cannot be undone.
    </Typography>
    <Button
      fullWidth variant="contained"
      onClick={onConfirm}
      sx={{
        backgroundColor: "#e24b4a", color: "#fff",
        textTransform: "none", borderRadius: "8px",
        "&:hover": { backgroundColor: "#c23a39" },
      }}
    >
      Yes, delete
    </Button>
  </Box>
);

/* ── Main component ── */
export default function PayrollSettings({ setIsSuccess }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);

  const { renumerationTypeList, setNewRenumerationTypeData } = useRenumerationTypesList(1, business_branch_id);
  const { allowanceTypeList,    setNewAllowanceTypeData }    = useAllowanceTypesList(1, business_branch_id);
  const { deductionTypeList,    setNewDeductionTypeData }    = useDeductionTypesList(1, business_branch_id);

  /* ── Renumeration state ── */
  const [openRenumeration,       setOpenRenumeration]       = useState(false);
  const [openEditRenumeration,   setOpenEditRenumeration]   = useState(false);
  const [openDeleteRenumeration, setOpenDeleteRenumeration] = useState(false);
  const [editRenumerationData,   setEditRenumerationData]   = useState({ name: "" });
  const [clickedRenumeration,    setClickedRenumeration]    = useState("");

  /* ── Allowance state ── */
  const [openAllowance,       setOpenAllowance]       = useState(false);
  const [openEditAllowance,   setOpenEditAllowance]   = useState(false);
  const [openDeleteAllowance, setOpenDeleteAllowance] = useState(false);
  const [editAllowanceData,   setEditAllowanceData]   = useState({ name: "" });
  const [clickedAllowance,    setClickedAllowance]    = useState("");

  /* ── Deduction state ── */
  const [openDeduction,       setOpenDeduction]       = useState(false);
  const [openEditDeduction,   setOpenEditDeduction]   = useState(false);
  const [openDeleteDeduction, setOpenDeleteDeduction] = useState(false);
  const [editDeductionData,   setEditDeductionData]   = useState({ name: "" });
  const [clickedDeduction,    setClickedDeduction]    = useState("");

  /* ── Renumeration handlers ── */
  const handleEditRenumeration = async (id) => {
    const res = await requester.get(`/human_resource/renumeration/get/${id}`);
    if (res.status === 200) { setEditRenumerationData(res.data.data); setOpenEditRenumeration(true); }
  };
  const handleDeleteOpenRenumeration = async (id) => {
    const res = await requester.get(`/human_resource/renumeration/get/${id}`);
    if (res.status === 200) { setClickedRenumeration(res.data.data.id); setOpenDeleteRenumeration(true); }
  };
  const handleDeleteRenumeration = async () => {
    await requester.post(`/human_resource/renumeration/delete/${clickedRenumeration}`);
    setIsSuccess(true); setNewRenumerationTypeData(true); setOpenDeleteRenumeration(false);
  };

  /* ── Allowance handlers ── */
  const handleEditAllowance = async (id) => {
    const res = await requester.get(`/human_resource/allowance/get/${id}`);
    if (res.status === 200) { setEditAllowanceData(res.data.data); setOpenEditAllowance(true); }
  };
  const handleDeleteOpenAllowance = async (id) => {
    const res = await requester.get(`/human_resource/allowance/get/${id}`);
    if (res.status === 200) { setClickedAllowance(res.data.data.id); setOpenDeleteAllowance(true); }
  };
  const handleDeleteAllowance = async () => {
    await requester.post(`/human_resource/allowance/delete/${clickedAllowance}`);
    setIsSuccess(true); setNewAllowanceTypeData(true); setOpenDeleteAllowance(false);
  };

  /* ── Deduction handlers ── */
  const handleEditDeduction = async (id) => {
    const res = await requester.get(`/human_resource/deduction/get/${id}`);
    if (res.status === 200) { setEditDeductionData(res.data.data); setOpenEditDeduction(true); }
  };
  const handleDeleteOpenDeduction = async (id) => {
    const res = await requester.get(`/human_resource/deduction/get/${id}`);
    if (res.status === 200) { setClickedDeduction(res.data.data.id); setOpenDeleteDeduction(true); }
  };
  const handleDeleteDeduction = async () => {
    await requester.post(`/human_resource/deduction/delete/${clickedDeduction}`);
    setIsSuccess(true); setNewDeductionTypeData(true); setOpenDeleteDeduction(false);
  };

  const dialogBg = { p: "1rem", borderRadius: "0.5rem", backgroundColor: theme.palette.background.alt };
  const sharedSectionProps = { cardBg, border, accent, textPri, textSec, isDark };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Page header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box sx={{
          width: "40px", height: "40px", borderRadius: "10px",
          backgroundColor: "rgba(29,158,117,0.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accent, flexShrink: 0,
        }}>
          <PaymentsIcon sx={{ fontSize: "22px" }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
            Payroll Settings
          </Typography>
          <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
            Configure remuneration, allowance and deduction types
          </Typography>
        </Box>
      </Box>

      {/* ── Renumeration ── */}
      <SettingsSection
        icon={<PaymentsIcon sx={{ fontSize: "16px" }} />}
        title="Renumeration Types"
        items={renumerationTypeList}
        nameKey="renumeration_name"
        onAdd={() => setOpenRenumeration(true)}
        onEdit={handleEditRenumeration}
        onDelete={handleDeleteOpenRenumeration}
        emptyMessage="No remuneration types yet. Add one to get started."
        {...sharedSectionProps}
      />

      {/* ── Allowances ── */}
      <SettingsSection
        icon={<CardGiftcardIcon sx={{ fontSize: "16px" }} />}
        title="Allowance Types"
        items={allowanceTypeList}
        nameKey="allowance_name"
        onAdd={() => setOpenAllowance(true)}
        onEdit={handleEditAllowance}
        onDelete={handleDeleteOpenAllowance}
        emptyMessage="No allowance types yet. Add one to get started."
        {...sharedSectionProps}
      />

      {/* ── Deductions ── */}
      <SettingsSection
        icon={<RemoveCircleIcon sx={{ fontSize: "16px" }} />}
        title="Deduction Types"
        items={deductionTypeList}
        nameKey="deduction_name"
        onAdd={() => setOpenDeduction(true)}
        onEdit={handleEditDeduction}
        onDelete={handleDeleteOpenDeduction}
        emptyMessage="No deduction types yet. Add one to get started."
        {...sharedSectionProps}
      />

      {/* ── Renumeration dialogs ── */}
      <FixedSizeDialog dialogHeading="Add Renumeration Type"
        open={openRenumeration} handleClickOpen={() => setOpenRenumeration(true)} handleClose={() => setOpenRenumeration(false)}
        negativeResponse="Cancel" maxWidth="md"
        content={<Box {...dialogBg}><RenumerationTypeForm formType="create" setOpen={setOpenRenumeration} setNewRenumerationTypeData={setNewRenumerationTypeData} setIsSuccess={setIsSuccess} editData={editRenumerationData} /></Box>}
      />
      <FixedSizeDialog dialogHeading="Edit Renumeration Type"
        open={openEditRenumeration} handleClickOpen={() => setOpenEditRenumeration(true)} handleClose={() => setOpenEditRenumeration(false)}
        negativeResponse="Cancel" maxWidth="md"
        content={<Box {...dialogBg}><RenumerationTypeForm formType="edit" setOpen={setOpenEditRenumeration} setNewRenumerationTypeData={setNewRenumerationTypeData} setIsSuccess={setIsSuccess} editData={editRenumerationData} /></Box>}
      />
      <FixedSizeDialog dialogHeading="Delete Renumeration Type?"
        open={openDeleteRenumeration} handleClickOpen={() => setOpenDeleteRenumeration(true)} handleClose={() => setOpenDeleteRenumeration(false)}
        negativeResponse="Cancel" maxWidth="sm"
        content={<DeleteContent onConfirm={handleDeleteRenumeration} />}
      />

      {/* ── Allowance dialogs ── */}
      <FixedSizeDialog dialogHeading="Add Allowance Type"
        open={openAllowance} handleClickOpen={() => setOpenAllowance(true)} handleClose={() => setOpenAllowance(false)}
        negativeResponse="Cancel" maxWidth="md"
        content={<Box {...dialogBg}><AllowanceTypeForm formType="create" setOpen={setOpenAllowance} setNewAllowanceTypeData={setNewAllowanceTypeData} setIsSuccess={setIsSuccess} editData={editAllowanceData} /></Box>}
      />
      <FixedSizeDialog dialogHeading="Edit Allowance Type"
        open={openEditAllowance} handleClickOpen={() => setOpenEditAllowance(true)} handleClose={() => setOpenEditAllowance(false)}
        negativeResponse="Cancel" maxWidth="md"
        content={<Box {...dialogBg}><AllowanceTypeForm formType="edit" setOpen={setOpenEditAllowance} setNewAllowanceTypeData={setNewAllowanceTypeData} setIsSuccess={setIsSuccess} editData={editAllowanceData} /></Box>}
      />
      <FixedSizeDialog dialogHeading="Delete Allowance Type?"
        open={openDeleteAllowance} handleClickOpen={() => setOpenDeleteAllowance(true)} handleClose={() => setOpenDeleteAllowance(false)}
        negativeResponse="Cancel" maxWidth="sm"
        content={<DeleteContent onConfirm={handleDeleteAllowance} />}
      />

      {/* ── Deduction dialogs ── */}
      <FixedSizeDialog dialogHeading="Add Deduction Type"
        open={openDeduction} handleClickOpen={() => setOpenDeduction(true)} handleClose={() => setOpenDeduction(false)}
        negativeResponse="Cancel" maxWidth="md"
        content={<Box {...dialogBg}><DeductionTypeForm formType="create" setOpen={setOpenDeduction} setNewDeductionTypeData={setNewDeductionTypeData} setIsSuccess={setIsSuccess} editData={editDeductionData} /></Box>}
      />
      <FixedSizeDialog dialogHeading="Edit Deduction Type"
        open={openEditDeduction} handleClickOpen={() => setOpenEditDeduction(true)} handleClose={() => setOpenEditDeduction(false)}
        negativeResponse="Cancel" maxWidth="md"
        content={<Box {...dialogBg}><DeductionTypeForm formType="edit" setOpen={setOpenEditDeduction} setNewDeductionTypeData={setNewDeductionTypeData} setIsSuccess={setIsSuccess} editData={editDeductionData} /></Box>}
      />
      <FixedSizeDialog dialogHeading="Delete Deduction Type?"
        open={openDeleteDeduction} handleClickOpen={() => setOpenDeleteDeduction(true)} handleClose={() => setOpenDeleteDeduction(false)}
        negativeResponse="Cancel" maxWidth="sm"
        content={<DeleteContent onConfirm={handleDeleteDeduction} />}
      />
    </Box>
  );
}